import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import QuickWorklogModal from './QuickWorklogModal';
import TicketDetailPanel from './TicketDetailPanel';
import TicketKeyActions from './TicketKeyActions';
import TimesheetModal from './TimesheetModal';
import InvoiceModal, { InvoicePrefill } from './InvoiceModal';
import { ToastContainer, ToastType } from './Toast';
import { JiraErrorBanner } from './JiraRateLimitBanner';
import { useProject } from '../context/ProjectContext';
import { notifyJiraError } from '../utils/jiraErrors';
import {
  buildStatusPickerItems,
  statusesMatch,
} from '../utils/jiraTransitions';
import { splitAssigneesForPicker } from '../utils/assigneePicker';
import {
  buildTimesheetsAppUrl,
  getSavedTimesheetsAppUrl,
  getTimesheetSubmitReady,
  markTimesheetPeriodSubmitted,
  saveTimesheetsAppUrl,
  type TimesheetSubmitPeriod,
} from '../utils/timesheetSubmit';
import { getHolidaysInMonth } from '../utils/czechHolidays';

const TICKETS_PANEL_WIDTH_KEY = 'complic_worklog_tickets_panel_width';
const TICKETS_PANEL_MIN = 260;
const TICKETS_PANEL_MAX = 704;
const WORKLOG_PANEL_MIN = 320;

const readTicketsPanelWidth = (): number => {
  try {
    const saved = localStorage.getItem(TICKETS_PANEL_WIDTH_KEY);
    if (saved) {
      const n = parseInt(saved, 10);
      if (!Number.isNaN(n)) {
        return Math.min(TICKETS_PANEL_MAX, Math.max(TICKETS_PANEL_MIN, n));
      }
    }
  } catch {
    /* ignore */
  }
  if (typeof window !== 'undefined') {
    return Math.min(TICKETS_PANEL_MAX, Math.max(TICKETS_PANEL_MIN, Math.round(window.innerWidth * 0.38)));
  }
  return 480;
};


interface WorklogTicket {
  key: string;
  summary: string;
  status: string;
  assignee: string | null;
  priority: string | null;
  labels: string[];
  sprint: string;
  my_time: number;
  total_time: number;
  by_day: { [day: number]: number };
}

interface SprintSummary {
  total_time: number;
  ticket_count: number;
}

interface WorklogData {
  total_time: number;
  tickets: WorklogTicket[];
  by_sprint: { [sprint: string]: SprintSummary };
  by_day: { [ticket: string]: { [day: number]: number } };
  daily_totals: { [day: number]: number };
  ticket_count: number;
  year: number;
  month: number;
  days_in_month: number;
}

interface CatalogTicket {
  key: string;
  summary: string;
  status: string;
  assignee: string | null;
  priority: string | null;
  labels: string[];
  sprint: string | null;
}

interface Transition {
  id: string;
  name: string;
  to?: string;
}

interface WorklogProps {
  onStartComplic?: (ticketKey: string, mode?: string) => void;
  /** Open ticket detail from Complic / deep link */
  openRequest?: { ticketKey: string; section?: string | null } | null;
  onOpenRequestHandled?: () => void;
}

interface TicketFilter {
  id: string;
  assignees: string[];
  statuses: string[];
  labels: string[];
  sprints: string[];
}

interface FilterGroup {
  id: string;
  name: string;
  filters: TicketFilter[];
}

const applyTicketFilter = (
  tickets: CatalogTicket[],
  filterGroups: FilterGroup[],
  selectedGroupId: string | null
): CatalogTicket[] => {
  if (!selectedGroupId) return tickets;
  const selectedGroup = filterGroups.find((g) => g.id === selectedGroupId);
  if (!selectedGroup || selectedGroup.filters.length === 0) return tickets;

  // Same logic as Tickets.getFilteredTickets (OR across filters, AND within filter)
  return tickets.filter((ticket) =>
    selectedGroup.filters.some((filter) => {
      const assigneeMatch =
        !filter.assignees ||
        filter.assignees.length === 0 ||
        (!!ticket.assignee && filter.assignees.includes(ticket.assignee));

      const statusMatch =
        !filter.statuses ||
        filter.statuses.length === 0 ||
        filter.statuses.some((s) => statusesMatch(s, ticket.status));

      const sprintMatch =
        !filter.sprints ||
        filter.sprints.length === 0 ||
        filter.sprints.some((s) => {
          if (s === 'Backlog') return !ticket.sprint;
          return ticket.sprint === s;
        });

      // Match Tickets.tsx: labels not applied yet (would hide half of BE filter)
      const labelMatch = !filter.labels || filter.labels.length === 0 || true;

      return assigneeMatch && statusMatch && sprintMatch && labelMatch;
    })
  );
};

const getInitials = (name: string | null) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
  return name.charAt(0).toUpperCase();
};

const getAvatarColor = (name: string | null) => {
  const palette = [
    'bg-blue-600',
    'bg-emerald-600',
    'bg-violet-600',
    'bg-rose-600',
    'bg-amber-600',
    'bg-cyan-600',
    'bg-indigo-600',
    'bg-pink-600',
    'bg-teal-600',
    'bg-orange-600',
    'bg-fuchsia-600',
    'bg-lime-700',
  ];
  if (!name) return 'bg-gray-500';
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
};

const getStatusColor = (status: string | null) => {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower.includes('open') || statusLower.includes('otevřeno') || statusLower.includes('to do')) {
    return 'bg-gray-200 text-gray-700';
  }
  if (statusLower.includes('rozpracováno') || statusLower.includes('in progress')) {
    return 'bg-blue-400 text-white';
  }
  if (statusLower.includes('revize') || statusLower.includes('review')) {
    return 'bg-blue-700 text-white';
  }
  if (statusLower.includes('test')) {
    return 'bg-blue-900 text-white';
  }
  if (statusLower.includes('done') || statusLower.includes('dokončeno')) {
    return 'bg-green-100 text-green-800';
  }
  if (statusLower.includes('ready for')) {
    return 'bg-sky-500 text-white';
  }
  return 'bg-gray-200 text-gray-700';
};

const getStatusSortValue = (status: string | null) => {
  const statusLower = status?.toLowerCase() || '';
  if (statusLower.includes('open') || statusLower.includes('otevřeno')) return 1;
  if (statusLower.includes('in progress') || statusLower.includes('rozpracováno')) return 2;
  if (statusLower.includes('ready for backend') || statusLower.includes('ready for frontend')) return 3;
  if (statusLower.includes('review') || statusLower.includes('revize')) return 4;
  if (statusLower.includes('test')) return 5;
  if (statusLower.includes('done') || statusLower.includes('dokončeno')) return 6;
  return 999;
};

const normalizePerson = (name: string | null) =>
  (name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/** Within a status: Marek first, then Martin, then everyone else. */
const getAssigneeSortValue = (assignee: string | null) => {
  const name = normalizePerson(assignee);
  if (name.includes('marek') && name.includes('senkyrik')) return 1;
  if (name.includes('martin') && name.includes('jandl')) return 2;
  return 3;
};

/** White bg for focus tickets; light gray for the rest (incl. Marek To Test). */
const isToTestStatus = (status: string | null) => {
  const s = (status || '').toLowerCase();
  return s.includes('to test') || s === 'test';
};

const isMarekSenkyrik = (assignee: string | null) => {
  const name = normalizePerson(assignee);
  return name.includes('marek') && name.includes('senkyrik');
};

const isHighlightedCatalogTicket = (ticket: CatalogTicket) => {
  const assignee = normalizePerson(ticket.assignee);
  const status = (ticket.status || '').toLowerCase();
  const isToTest = isToTestStatus(ticket.status);

  if (isMarekSenkyrik(ticket.assignee)) {
    return !isToTest;
  }

  if (assignee.includes('martin') && assignee.includes('jandl')) {
    const isOpen = status.includes('open') || status.includes('otevřeno');
    const isReadyBackend = status.includes('ready for backend');
    return isOpen || isReadyBackend;
  }

  return false;
};

const getPrioritySortValue = (priority: string | null): number => {
  switch (priority?.toLowerCase()) {
    case 'highest': return 1;
    case 'high': return 2;
    case 'medium': return 3;
    case 'low': return 4;
    case 'lowest': return 5;
    default: return 999;
  }
};

const Worklog: React.FC<WorklogProps> = ({
  onStartComplic,
  openRequest = null,
  onOpenRequestHandled,
}) => {
  const { currentProject, jiraUrl } = useProject();
  const [worklogData, setWorklogData] = useState<WorklogData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Ticket detail panel (side panel like Tickets)
  const [selectedTicketKey, setSelectedTicketKey] = useState<string | null>(null);
  const [selectedTicketData, setSelectedTicketData] = useState<CatalogTicket | WorklogTicket | null>(null);
  const [detailExpandedSection, setDetailExpandedSection] = useState<
    'plan' | 'implement' | 'push' | 'log_work' | 'review' | null
  >(null);

  // Status transitions in the right-hand ticket list (same UX as Tickets)
  const [listTransitions, setListTransitions] = useState<{
    [key: string]: Array<Transition & { to?: string }>;
  }>({});
  const [loadingListTransitions, setLoadingListTransitions] = useState<string | null>(null);
  const [showListTransitions, setShowListTransitions] = useState<string | null>(null);
  const [showComplicMenu, setShowComplicMenu] = useState<string | null>(null);
  const [showAssigneeMenu, setShowAssigneeMenu] = useState<string | null>(null);
  const [showOtherAssignees, setShowOtherAssignees] = useState(false);
  const [updatingAssignee, setUpdatingAssignee] = useState(false);
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [showInvoice, setShowInvoice] = useState(false);
  const [invoicePrefill, setInvoicePrefill] = useState<InvoicePrefill | null>(null);
  const [timesheetSubmitPeriod, setTimesheetSubmitPeriod] = useState<TimesheetSubmitPeriod | null>(
    () => getTimesheetSubmitReady()
  );
  const [confirmTimesheetSubmitted, setConfirmTimesheetSubmitted] = useState(false);
  const [showTimesheetsUrlSetup, setShowTimesheetsUrlSetup] = useState(false);
  const [timesheetsUrlDraft, setTimesheetsUrlDraft] = useState('');
  const [timesheetsUrlError, setTimesheetsUrlError] = useState<string | null>(null);

  // Current date for default month
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1); // 1-based

  // Quick worklog modal for specific day
  const [quickWorklog, setQuickWorklog] = useState<{ticketKey: string, day: number} | null>(null);

  // Full ticket catalog (all project tickets) for search + filter list
  const [allTickets, setAllTickets] = useState<CatalogTicket[]>([]);
  const [filterGroups, setFilterGroups] = useState<FilterGroup[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() => {
    try {
      return localStorage.getItem('complic_selected_filter_group_id');
    } catch {
      return null;
    }
  });
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [projectStatuses, setProjectStatuses] = useState<string[]>([]);

  // Resizable worklog ↔ tickets split
  const layoutRef = useRef<HTMLDivElement>(null);
  const ticketsPanelWidthRef = useRef(readTicketsPanelWidth());
  const [ticketsPanelWidth, setTicketsPanelWidth] = useState(ticketsPanelWidthRef.current);
  const [isResizingPanels, setIsResizingPanels] = useState(false);

  // Create ticket
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [createSummary, setCreateSummary] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createIssueType, setCreateIssueType] = useState('Task');
  const [createLabels, setCreateLabels] = useState('BE');
  const [createSprintId, setCreateSprintId] = useState<number | ''>('');
  const [sprintOptions, setSprintOptions] = useState<
    Array<{ id: number; name: string; state: string }>
  >([]);
  const [loadingSprints, setLoadingSprints] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);

  // Ticket search → log time
  const [ticketSearch, setTicketSearch] = useState('');
  const searchWrapRef = useRef<HTMLDivElement>(null);

  // Quick actions dropdown for day
  const [quickActionsDay, setQuickActionsDay] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{top: number, left: number} | null>(null);

  // Handle ticket selection — open detail panel
  const handleTicketSelect = (
    ticket: CatalogTicket | WorklogTicket,
    section: string | null = null
  ) => {
    setSelectedTicketKey(ticket.key);
    setSelectedTicketData(ticket);
    setShowListTransitions(null);
    setShowAssigneeMenu(null);
    setShowComplicMenu(null);
    const allowed = ['plan', 'implement', 'push', 'log_work', 'review'] as const;
    setDetailExpandedSection(
      section && (allowed as readonly string[]).includes(section)
        ? (section as (typeof allowed)[number])
        : null
    );
  };

  // Open ticket from Complic / deep link
  useEffect(() => {
    if (!openRequest?.ticketKey) return;

    const key = openRequest.ticketKey.toUpperCase();
    const section = openRequest.section || null;
    const found =
      allTickets.find((t) => t.key.toUpperCase() === key) ||
      worklogData?.tickets.find((t) => t.key.toUpperCase() === key);

    if (found) {
      handleTicketSelect(found, section);
      onOpenRequestHandled?.();
      return;
    }

    if (loadingCatalog) return;

    const stub: CatalogTicket = {
      key: openRequest.ticketKey,
      summary: openRequest.ticketKey,
      status: '',
      assignee: null,
      priority: null,
      labels: [],
      sprint: null,
    };
    handleTicketSelect(stub, section);
    onOpenRequestHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest, allTickets, worklogData, loadingCatalog]);

  const fetchListTransitions = async (ticketKey: string) => {
    if (listTransitions[ticketKey]) return;

    setLoadingListTransitions(ticketKey);
    try {
      const response = await axios.get(`${API_URL}/api/tickets/${ticketKey}/transitions`, {
        params: { project_id: currentProject?.id },
      });
      setListTransitions((prev) => ({
        ...prev,
        [ticketKey]: response.data.transitions || [],
      }));
    } catch (error) {
      console.error('Failed to fetch transitions:', error);
      addToast(notifyJiraError(error), 'error');
      setShowListTransitions((prev) => (prev === ticketKey ? null : prev));
    } finally {
      setLoadingListTransitions((prev) => (prev === ticketKey ? null : prev));
    }
  };

  const handleStatusClick = (ticketKey: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setShowAssigneeMenu(null);
    setShowComplicMenu(null);
    if (showListTransitions === ticketKey) {
      setShowListTransitions(null);
    } else {
      // Open immediately; fetch in background (lightweight transitions endpoint)
      setShowListTransitions(ticketKey);
      void fetchListTransitions(ticketKey);
    }
  };

  const applyAssigneeLocally = (ticketKey: string, assignee: string | null) => {
    setAllTickets((prev) =>
      prev.map((t) => (t.key === ticketKey ? { ...t, assignee } : t))
    );
    if (selectedTicketKey === ticketKey) {
      setSelectedTicketData((prev) => (prev ? { ...prev, assignee } : prev));
    }
  };

  const applySprintLocally = (ticketKey: string, sprint: string | null) => {
    setAllTickets((prev) =>
      prev.map((t) => (t.key === ticketKey ? { ...t, sprint } : t))
    );
    if (selectedTicketKey === ticketKey) {
      setSelectedTicketData((prev) => (prev ? { ...prev, sprint } : prev));
    }
  };

  const applySummaryLocally = (ticketKey: string, summary: string) => {
    setAllTickets((prev) =>
      prev.map((t) => (t.key === ticketKey ? { ...t, summary } : t))
    );
    if (selectedTicketKey === ticketKey) {
      setSelectedTicketData((prev) => (prev ? { ...prev, summary } : prev));
    }
  };

  const handleAssigneeChange = async (
    ticketKey: string,
    assignee: string | null,
    event?: React.MouseEvent
  ) => {
    event?.stopPropagation();
    if (!currentProject?.id) return;
    setUpdatingAssignee(true);
    try {
      await axios.put(`${API_URL}/api/tickets/${ticketKey}/update`, null, {
        params: {
          project_id: currentProject.id,
          assignee: assignee ?? 'none',
        },
      });
      applyAssigneeLocally(ticketKey, assignee);
      setShowAssigneeMenu(null);
      addToast(
        assignee ? `${ticketKey} → ${assignee}` : `${ticketKey} unassigned`,
        'success'
      );
    } catch (error) {
      addToast(notifyJiraError(error), 'error');
    } finally {
      setUpdatingAssignee(false);
    }
  };

  const refreshCatalog = async () => {
    if (!currentProject) return;
    try {
      const ticketsRes = await axios.get(`${API_URL}/api/tickets/`, {
        params: { project_id: currentProject.id },
      });
      const list: CatalogTicket[] = (ticketsRes.data || []).map((t: any) => ({
        key: t.key,
        summary: t.summary || '',
        status: t.status || '',
        assignee: t.assignee ?? null,
        priority: t.priority ?? null,
        labels: t.labels || [],
        sprint: t.sprint ?? null,
      }));
      setAllTickets(list);

      if (selectedTicketKey) {
        const updated = list.find((t) => t.key === selectedTicketKey);
        if (updated) {
          setSelectedTicketData(updated);
        }
      }
    } catch (err) {
      console.error('Error refreshing ticket catalog:', err);
    }
  };

  const handleListTransition = async (
    ticketKey: string,
    transitionId: string,
    transitionName: string,
    event: React.MouseEvent,
    toStatus?: string
  ) => {
    event.stopPropagation();

    try {
      const res = await axios.post(`${API_URL}/api/tickets/${ticketKey}/transition`, null, {
        params: { transition_id: transitionId, project_id: currentProject?.id },
      });
      const newStatus = res.data?.status || toStatus || transitionName;

      // Update list + open detail immediately (don't wait for catalog cache)
      setAllTickets((prev) =>
        prev.map((t) => (t.key === ticketKey ? { ...t, status: newStatus } : t))
      );
      if (selectedTicketKey === ticketKey) {
        setSelectedTicketData((prev) => (prev ? { ...prev, status: newStatus } : prev));
      }

      setShowListTransitions(null);
      setListTransitions((prev) => {
        const next = { ...prev };
        delete next[ticketKey];
        return next;
      });
      addToast(`${ticketKey} → ${newStatus}`, 'success');
      // Background refresh — don't block UI
      void refreshCatalog();
    } catch (error) {
      console.error('Failed to transition ticket:', error);
      addToast(notifyJiraError(error), 'error');
    }
  };

  const getDefaultLogDay = () => {
    if (
      selectedYear === now.getFullYear() &&
      selectedMonth === now.getMonth() + 1
    ) {
      return now.getDate();
    }
    return Math.min(now.getDate(), 28);
  };

  const openLogForTicket = (ticketKey: string, clearSearch: boolean = false) => {
    setQuickWorklog({ ticketKey, day: getDefaultLogDay() });
    if (clearSearch) setTicketSearch('');
  };

  // Drag & drop state
  const [dragSource, setDragSource] = useState<{ticketKey: string, day: number} | null>(null);
  const [isMoving, setIsMoving] = useState(false); // Background loading for drag & drop
  const [isRefreshing, setIsRefreshing] = useState(false); // Silent background sync

  // Toast notifications
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: ToastType }>>([]);

  const addToast = (message: string, type: ToastType) => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  // Quick actions for recurring tickets
  const handleQuickAction = async (ticketKey: string, hours: number, comment: string, day: number) => {
    // Close modal immediately
    setQuickActionsDay(null);

    try {
      addToast(`Logging ${hours}h to ${ticketKey}...`, 'info');

      const targetDate = new Date(selectedYear, selectedMonth - 1, day);
      targetDate.setHours(10, 0, 0, 0);

      const pad = (n: number) => String(n).padStart(2, '0');
      const started = `${targetDate.getFullYear()}-${pad(targetDate.getMonth() + 1)}-${pad(targetDate.getDate())}T${pad(targetDate.getHours())}:${pad(targetDate.getMinutes())}:${pad(targetDate.getSeconds())}.000+0000`;

      await axios.post(`${API_URL}/api/tickets/${ticketKey}/worklog`, null, {
        params: {
          time_spent: hours.toString(),
          started: started,
          comment: comment,
          project_id: currentProject?.id
        }
      });

      addToast(`Logged ${hours}h to ${ticketKey}`, 'success');

      // Refresh silently
      fetchWorklog(true);
    } catch (err: any) {
      addToast(notifyJiraError(err), 'error');
    }
  };

  useEffect(() => {
    // Month / project change: full reload with skeleton. Mutations use fetchWorklog(true).
    setWorklogData(null);
    fetchWorklog(false);
  }, [selectedYear, selectedMonth, currentProject]);

  // Re-check previous-month submit CTA when tab becomes visible
  useEffect(() => {
    const refresh = () => setTimesheetSubmitPeriod(getTimesheetSubmitReady());
    refresh();
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  // Load ALL project tickets + same filter groups as Tickets (for search + compact list)
  useEffect(() => {
    if (!currentProject) {
      setAllTickets([]);
      setFilterGroups([]);
      setProjectStatuses([]);
      return;
    }

    let cancelled = false;
    const loadCatalog = async () => {
      setLoadingCatalog(true);
      try {
        const [ticketsRes, filtersRes, optionsRes] = await Promise.all([
          axios.get(`${API_URL}/api/tickets/`, { params: { project_id: currentProject.id } }),
          axios.get(`${API_URL}/api/auth/filters`),
          axios
            .get(`${API_URL}/api/tickets/filters/options`, {
              params: { project_id: currentProject.id },
            })
            .catch(() => ({ data: { statuses: [] } })),
        ]);
        if (cancelled) return;

        const list: CatalogTicket[] = (ticketsRes.data || []).map((t: any) => ({
          key: t.key,
          summary: t.summary || '',
          status: t.status || '',
          assignee: t.assignee ?? null,
          priority: t.priority ?? null,
          labels: t.labels || [],
          sprint: t.sprint ?? null,
        }));
        setAllTickets(list);

        const fromOptions: string[] = optionsRes.data?.statuses || [];
        const fromTickets = list.map((t) => t.status).filter(Boolean) as string[];
        setProjectStatuses(
          Array.from(new Set([...fromOptions, ...fromTickets])).sort((a, b) =>
            a.localeCompare(b, 'cs')
          )
        );

        const groups: FilterGroup[] = filtersRes.data?.filterGroups || [];
        setFilterGroups(groups);

        const savedId = (() => {
          try {
            return localStorage.getItem('complic_selected_filter_group_id');
          } catch {
            return null;
          }
        })();
        const validSaved = savedId && groups.some((g) => g.id === savedId) ? savedId : null;
        if (validSaved) {
          setSelectedGroupId(validSaved);
        } else if (groups.length > 0) {
          setSelectedGroupId(groups[0].id);
        } else {
          setSelectedGroupId(null);
        }
      } catch (err) {
        console.error('Error loading ticket catalog for worklog:', err);
        if (!cancelled) {
          addToast(notifyJiraError(err), 'error');
        }
      } finally {
        if (!cancelled) setLoadingCatalog(false);
      }
    };

    void loadCatalog();
    return () => {
      cancelled = true;
    };
  }, [currentProject]);

  // Close status / assignee menus on outside click
  useEffect(() => {
    const onDocClick = () => {
      setShowListTransitions(null);
      setShowComplicMenu(null);
      setShowAssigneeMenu(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const persistTicketsPanelWidth = useCallback((width: number) => {
    ticketsPanelWidthRef.current = width;
    setTicketsPanelWidth(width);
    try {
      localStorage.setItem(TICKETS_PANEL_WIDTH_KEY, String(width));
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isResizingPanels) return;

    const onMove = (e: MouseEvent) => {
      const layout = layoutRef.current;
      if (!layout) return;
      const rect = layout.getBoundingClientRect();
      const padRight = parseFloat(getComputedStyle(layout).paddingRight) || 0;
      // Tickets panel is on the right — width from mouse to content's right edge
      const raw = Math.round(rect.right - padRight - e.clientX);
      const contentWidth = rect.width - padRight - (parseFloat(getComputedStyle(layout).paddingLeft) || 0);
      const maxAllowed = Math.min(
        TICKETS_PANEL_MAX,
        Math.max(TICKETS_PANEL_MIN, contentWidth - WORKLOG_PANEL_MIN)
      );
      const next = Math.min(maxAllowed, Math.max(TICKETS_PANEL_MIN, raw));
      ticketsPanelWidthRef.current = next;
      setTicketsPanelWidth(next);
    };

    const onUp = () => {
      setIsResizingPanels(false);
      persistTicketsPanelWidth(ticketsPanelWidthRef.current);
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isResizingPanels, persistTicketsPanelWidth]);

  const openCreateTicketModal = async () => {
    setShowCreateTicket(true);
    setCreateSummary('');
    setCreateDescription('');
    setCreateIssueType('Task');
    setCreateLabels('BE');
    setCreateSprintId('');
    if (!currentProject) return;

    setLoadingSprints(true);
    try {
      const res = await axios.get(`${API_URL}/api/tickets/sprints`, {
        params: { project_id: currentProject.id },
      });
      const list: Array<{ id: number; name: string; state: string }> =
        res.data?.sprints || [];
      setSprintOptions(list);
      const activeId = res.data?.active_sprint_id as number | null | undefined;
      if (activeId) {
        setCreateSprintId(activeId);
      } else if (list.length > 0) {
        setCreateSprintId(list[0].id);
      } else {
        setCreateSprintId('');
      }
    } catch (err) {
      console.error('Error loading sprints:', err);
      setSprintOptions([]);
      setCreateSprintId('');
      addToast('Nepodařilo se načíst sprinty z Jiry', 'error');
    } finally {
      setLoadingSprints(false);
    }
  };

  const handleCreateTicket = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const summary = createSummary.trim();
    if (!summary || !currentProject || creatingTicket) return;

    const labelList = (createLabels.trim() || 'BE')
      .split(',')
      .map((l) => l.trim())
      .filter(Boolean);

    const sprintName =
      createSprintId === ''
        ? null
        : sprintOptions.find((s) => s.id === createSprintId)?.name || null;

    setCreatingTicket(true);
    try {
      const res = await axios.post(`${API_URL}/api/tickets/create`, null, {
        params: {
          project_id: currentProject.id,
          summary,
          description: createDescription.trim() || undefined,
          issue_type: createIssueType || 'Task',
          labels: labelList.join(',') || 'BE',
          assignee: 'me',
          ...(createSprintId !== ''
            ? { sprint_id: createSprintId }
            : { to_backlog: true }),
        },
      });
      const key = res.data?.key as string | undefined;
      addToast(key ? `Tiket ${key} vytvořen` : 'Tiket vytvořen', 'success');
      setShowCreateTicket(false);
      setCreateSummary('');
      setCreateDescription('');
      setCreateIssueType('Task');
      setCreateLabels('BE');
      setCreateSprintId('');
      await refreshCatalog();
      if (key) {
        handleTicketSelect({
          key,
          summary,
          status: 'To Do',
          assignee: null,
          priority: null,
          labels: labelList,
          sprint: res.data?.sprint || sprintName,
        });
      }
    } catch (err) {
      addToast(notifyJiraError(err), 'error');
    } finally {
      setCreatingTicket(false);
    }
  };

  const ticketsInActiveFilter = applyTicketFilter(
    allTickets,
    filterGroups,
    selectedGroupId
  ).filter(
    (ticket) => !isToTestStatus(ticket.status) || isMarekSenkyrik(ticket.assignee)
  );

  const filteredCatalogTickets = ticketsInActiveFilter.filter((ticket) => {
    const q = ticketSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      ticket.key.toLowerCase().includes(q) ||
      ticket.summary.toLowerCase().includes(q)
    );
  });

  /** Search hits outside the active filter group (e.g. FE when filter is BE). */
  const outOfFilterSearchHits = (() => {
    const q = ticketSearch.trim().toLowerCase();
    if (!q) return [] as CatalogTicket[];
    const inFilterKeys = new Set(ticketsInActiveFilter.map((t) => t.key));
    return allTickets
      .filter(
        (t) =>
          !inFilterKeys.has(t.key) &&
          (t.key.toLowerCase().includes(q) || t.summary.toLowerCase().includes(q))
      )
      .sort((a, b) => {
        const numA = parseInt(a.key.split('-')[1] || '0', 10);
        const numB = parseInt(b.key.split('-')[1] || '0', 10);
        return numA - numB;
      })
      .slice(0, 40);
  })();

  const catalogAssignees = Array.from(
    new Set(allTickets.map((t) => t.assignee).filter((a): a is string => !!a))
  ).sort((a, b) => a.localeCompare(b, 'cs'));

  const visibleAssignees = Array.from(
    new Set(
      filteredCatalogTickets
        .map((t) => t.assignee)
        .filter((a): a is string => !!a)
    )
  );

  const { preferred: preferredAssignees, others: otherAssignees } =
    splitAssigneesForPicker(catalogAssignees, {
      filterGroups,
      selectedGroupId,
      visibleAssignees,
    });

  // Group by sprint (Backlog last) — same sort as Tickets: status then priority
  const catalogBySprint = (() => {
    const groups: { [sprint: string]: CatalogTicket[] } = {};
    for (const ticket of filteredCatalogTickets) {
      const sprintName = ticket.sprint || 'Backlog';
      if (!groups[sprintName]) groups[sprintName] = [];
      groups[sprintName].push(ticket);
    }
    Object.keys(groups).forEach((sprintName) => {
      groups[sprintName].sort((a, b) => {
        const statusDiff = getStatusSortValue(a.status) - getStatusSortValue(b.status);
        if (statusDiff !== 0) return statusDiff;
        const assigneeDiff = getAssigneeSortValue(a.assignee) - getAssigneeSortValue(b.assignee);
        if (assigneeDiff !== 0) return assigneeDiff;
        return getPrioritySortValue(a.priority) - getPrioritySortValue(b.priority);
      });
    });
    return groups;
  })();

  const catalogSprintNames = Object.keys(catalogBySprint).sort((a, b) => {
    if (a === 'Backlog') return 1;
    if (b === 'Backlog') return -1;
    return a.localeCompare(b);
  });

  const selectFilterGroup = (groupId: string | null) => {
    setSelectedGroupId(groupId);
    try {
      if (groupId) {
        localStorage.setItem('complic_selected_filter_group_id', groupId);
      } else {
        localStorage.removeItem('complic_selected_filter_group_id');
      }
    } catch {
      /* ignore */
    }
  };

  const ticketSearchResults = (() => {
    const q = ticketSearch.trim().toLowerCase();
    if (!q) return [] as CatalogTicket[];

    // Prefer in-filter hits, then out-of-filter — used for Enter → log
    const inFilter = filteredCatalogTickets.slice(0, 12);
    if (inFilter.length > 0) return inFilter;
    return outOfFilterSearchHits.slice(0, 12);
  })();

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setTicketSearch('');
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const q = ticketSearch.trim();
    if (!q) return;

    // Exact key match first
    const exact = ticketSearchResults.find(
      (t) => t.key.toLowerCase() === q.toLowerCase()
    );
    if (exact) {
      openLogForTicket(exact.key, true);
      return;
    }
    if (ticketSearchResults.length === 1) {
      openLogForTicket(ticketSearchResults[0].key, true);
      return;
    }
    // Allow typing a ticket key directly (e.g. PM3-1234)
    if (/^[A-Za-z][A-Za-z0-9]+-\d+$/.test(q)) {
      openLogForTicket(q.toUpperCase(), true);
    }
  };

  const fetchWorklog = async (silent: boolean = false) => {
    try {
      if (silent) {
        setIsRefreshing(true);
      } else {
        setLoading(true);
      }

      // Check if project is selected
      if (!currentProject) {
        setError('No project selected. Please select a project from the dropdown in the navbar.');
        setLoading(false);
        setIsRefreshing(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/tickets/worklog`, {
        params: {
          project_id: currentProject.id,
          year: selectedYear,
          month: selectedMonth
        }
      });
      setWorklogData(response.data);
      setError(null);
    } catch (err: any) {
      // Don't wipe the table on background sync failures
      if (!silent) {
        setError(notifyJiraError(err));
      } else {
        addToast(notifyJiraError(err), 'error');
      }
      console.error('Error fetching worklog:', err);
    } finally {
      if (silent) {
        setIsRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  const changeMonth = (delta: number) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;

    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const formatTime = (seconds: number) => {
    if (!seconds) return '-';
    const hours = seconds / 3600;
    // Remove unnecessary trailing zeros: 1.00 -> 1, 3.50 -> 3.5, 0.05 -> 0.05
    return parseFloat(hours.toFixed(2)).toString();
  };

  const handleDragStart = (e: React.DragEvent, ticketKey: string, day: number, timeLogged: number) => {
    if (timeLogged > 0) {
      setDragSource({ ticketKey, day });
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/html', e.currentTarget.innerHTML);
      // Make it visible that we're dragging
      (e.target as HTMLElement).style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Allow drop
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetTicketKey: string, targetDay: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (!dragSource) return;

    // Can't drop on same cell
    if (dragSource.ticketKey === targetTicketKey && dragSource.day === targetDay) {
      setDragSource(null);
      return;
    }

    // Find source ticket data
    const sourceTicket = worklogData?.tickets.find(t => t.key === dragSource.ticketKey);
    if (!sourceTicket) {
      setDragSource(null);
      return;
    }

    const timeToMove = sourceTicket.by_day[dragSource.day];
    if (!timeToMove) {
      setDragSource(null);
      return;
    }

    try {
      setIsMoving(true); // Show small spinner, don't block UI
      addToast('Moving worklog...', 'info');

      // Get all worklogs for source date
      const response = await axios.get(`${API_URL}/api/tickets/${dragSource.ticketKey}/worklogs`, {
        params: {
          year: selectedYear,
          month: selectedMonth,
          day: dragSource.day,
          project_id: currentProject?.id
        }
      });

      // API returns array directly or object with worklogs property
      const worklogsData = Array.isArray(response.data) ? response.data : (response.data.worklogs || []);

      if (!worklogsData || worklogsData.length === 0) {
        addToast('No worklogs found to move', 'error');
        setDragSource(null);
        setIsMoving(false);
        return;
      }

      // Move all worklogs from source day to target day
      // Format: "2024-06-26T10:00:00.000+0000" (Jira expects this exact format)
      const targetDate = new Date(selectedYear, selectedMonth - 1, targetDay);

      // Set time to match original worklog time (or default to 10:00)
      const sourceWorklog = worklogsData[0];
      if (sourceWorklog.started) {
        const sourceDate = new Date(sourceWorklog.started);
        targetDate.setHours(sourceDate.getHours(), sourceDate.getMinutes(), sourceDate.getSeconds());
      } else {
        targetDate.setHours(10, 0, 0, 0); // Default to 10:00 AM
      }

      // Format to Jira's expected format: yyyy-MM-dd'T'HH:mm:ss.SSSZ
      const pad = (n: number) => String(n).padStart(2, '0');
      const targetDateStr = `${targetDate.getFullYear()}-${pad(targetDate.getMonth() + 1)}-${pad(targetDate.getDate())}T${pad(targetDate.getHours())}:${pad(targetDate.getMinutes())}:${pad(targetDate.getSeconds())}.000+0000`;

      for (const worklog of worklogsData) {
        // Delete from source
        await axios.delete(`${API_URL}/api/tickets/${dragSource.ticketKey}/worklog/${worklog.id}`, {
          params: { project_id: currentProject?.id }
        });

        // Create on target
        await axios.post(`${API_URL}/api/tickets/${targetTicketKey}/worklog`, null, {
          params: {
            time_spent: formatTime(worklog.timeSpentSeconds),
            started: targetDateStr,
            comment: worklog.comment?.content?.[0]?.content?.[0]?.text || undefined,
            project_id: currentProject?.id
          }
        });
      }

      // Update local state immediately (optimistic update)
      setWorklogData(prevData => {
        if (!prevData) return prevData;

        const updatedTickets = prevData.tickets.map(ticket => {
          if (ticket.key === dragSource.ticketKey) {
            // Remove time from source
            const newByDay = { ...ticket.by_day };
            delete newByDay[dragSource.day];
            return {
              ...ticket,
              by_day: newByDay,
              my_time: ticket.my_time - timeToMove
            };
          } else if (ticket.key === targetTicketKey) {
            // Add time to target
            const newByDay = { ...ticket.by_day };
            newByDay[targetDay] = (newByDay[targetDay] || 0) + timeToMove;
            return {
              ...ticket,
              by_day: newByDay,
              my_time: ticket.my_time + timeToMove
            };
          }
          return ticket;
        });

        // Update daily totals
        const newDailyTotals = { ...prevData.daily_totals };
        newDailyTotals[dragSource.day] = (newDailyTotals[dragSource.day] || 0) - timeToMove;
        newDailyTotals[targetDay] = (newDailyTotals[targetDay] || 0) + timeToMove;

        return {
          ...prevData,
          tickets: updatedTickets,
          daily_totals: newDailyTotals
        };
      });

      setDragSource(null);
      setIsMoving(false);

      // Show success toast
      addToast(`Moved ${formatTime(timeToMove)}h from ${dragSource.ticketKey} (day ${dragSource.day}) to ${targetTicketKey} (day ${targetDay})`, 'success');

      // Verify with Jira in background (silent check - no loading spinner)
      fetchWorklog(true);
    } catch (err: any) {
      addToast(notifyJiraError(err), 'error');
      setDragSource(null);
      setIsMoving(false);
    }
  };

  // Sort tickets by key number (PM3-2124 -> 2124); apply search filter
  const sortedTickets = worklogData
    ? [...worklogData.tickets]
        .filter((t) => {
          const q = ticketSearch.trim().toLowerCase();
          if (!q) return true;
          return (
            t.key.toLowerCase().includes(q) ||
            t.summary.toLowerCase().includes(q)
          );
        })
        .sort((a, b) => {
          const numA = parseInt(a.key.split('-')[1] || '0');
          const numB = parseInt(b.key.split('-')[1] || '0');
          return numA - numB;
        })
    : [];

  // Daily totals from currently visible (filtered) rows
  const visibleDailyTotals: { [day: number]: number } = {};
  let visibleTotalTime = 0;
  for (const ticket of sortedTickets) {
    visibleTotalTime += ticket.my_time || 0;
    Object.entries(ticket.by_day || {}).forEach(([day, seconds]) => {
      const d = Number(day);
      visibleDailyTotals[d] = (visibleDailyTotals[d] || 0) + Number(seconds);
    });
  }

  const holidaysInMonth = getHolidaysInMonth(selectedYear, selectedMonth);
  const daysInMonth =
    worklogData?.days_in_month ?? new Date(selectedYear, selectedMonth, 0).getDate();

  return (
    <>
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {showTimesheet && worklogData && (
        <TimesheetModal
          year={selectedYear}
          month={selectedMonth}
          tickets={worklogData.tickets.map((t) => {
            const byDayHours: { [day: number]: number } = {};
            Object.entries(t.by_day || {}).forEach(([day, seconds]) => {
              const h = Math.round((Number(seconds) / 3600) * 100) / 100;
              if (h > 0) byDayHours[Number(day)] = h;
            });
            return {
              key: t.key,
              summary: t.summary,
              hours: Math.round((t.my_time / 3600) * 100) / 100,
              by_day: byDayHours,
            };
          })}
          onClose={() => setShowTimesheet(false)}
          onCreateInvoice={(preview) => {
            setShowTimesheet(false);
            setInvoicePrefill({
              suggestedItem: preview.suggestedItem,
              dateCreated: preview.dateCreated,
              dateDue: preview.dateDue,
              defaults: preview.defaults,
              totalHours: preview.totalHours,
              totalAmount: preview.totalAmount,
              hourlyRate: preview.hourlyRate,
              periodLabel: `${String(selectedMonth).padStart(2, '0')}/${selectedYear}`,
            });
            setShowInvoice(true);
          }}
        />
      )}

      {showInvoice && (
        <InvoiceModal
          prefill={invoicePrefill}
          onClose={() => {
            setShowInvoice(false);
            setInvoicePrefill(null);
          }}
          onSuccess={(invoice) => {
            addToast(
              `Faktura ${invoice.invoice_number || invoice.id} vytvořena`,
              'success'
            );
          }}
        />
      )}

      {showCreateTicket && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
          onClick={() => !creatingTicket && setShowCreateTicket(false)}
        >
          <form
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-panel border border-slate-200 dark:border-slate-700 w-full max-w-lg p-5 space-y-4"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleCreateTicket}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-extrabold tracking-tight text-slate-900 dark:text-white">
                  Nový tiket
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Výchozí: aktivní sprint, label BE, zadavatel i zpracovatel = já
                </p>
              </div>
              <button
                type="button"
                onClick={() => !creatingTicket && setShowCreateTicket(false)}
                className="text-slate-400 hover:text-slate-700 dark:hover:text-white text-xl leading-none px-1"
                aria-label="Zavřít"
              >
                ×
              </button>
            </div>

            <label className="block space-y-1">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                Název *
              </span>
              <input
                autoFocus
                type="text"
                value={createSummary}
                onChange={(e) => setCreateSummary(e.target.value)}
                placeholder="Krátký popis tiketu…"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-complic-400/50 focus:border-complic-400"
                disabled={creatingTicket}
                required
              />
            </label>

            <label className="block space-y-1">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                Popis
              </span>
              <textarea
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="Volitelný popis…"
                rows={3}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-complic-400/50 focus:border-complic-400 resize-y"
                disabled={creatingTicket}
              />
            </label>

            <label className="block space-y-1">
              <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                Sprint
              </span>
              <select
                value={createSprintId === '' ? '' : String(createSprintId)}
                onChange={(e) => {
                  const v = e.target.value;
                  setCreateSprintId(v === '' ? '' : Number(v));
                }}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-complic-400/50"
                disabled={creatingTicket || loadingSprints}
              >
                <option value="">Backlog</option>
                {sprintOptions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.state === 'active'
                      ? ' (aktivní)'
                      : s.state === 'future'
                        ? ' (budoucí)'
                        : s.state === 'closed'
                          ? ' (uzavřený)'
                          : ''}
                  </option>
                ))}
              </select>
              {loadingSprints && (
                <span className="text-[11px] text-slate-400">Načítám sprinty…</span>
              )}
              {!loadingSprints && sprintOptions.length === 0 && (
                <span className="text-[11px] text-amber-600 dark:text-amber-400">
                  Žádné aktivní/future sprinty — zkontroluj Scrum board v Jiře
                </span>
              )}
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block space-y-1">
                <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Typ
                </span>
                <select
                  value={createIssueType}
                  onChange={(e) => setCreateIssueType(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-complic-400/50"
                  disabled={creatingTicket}
                >
                  <option value="Task">Task</option>
                  <option value="Bug">Bug</option>
                  <option value="Story">Story</option>
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                  Labels
                </span>
                <input
                  type="text"
                  value={createLabels}
                  onChange={(e) => setCreateLabels(e.target.value)}
                  placeholder="BE, FE"
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-complic-400/50"
                  disabled={creatingTicket}
                />
              </label>
            </div>

            <div className="flex flex-wrap gap-2 text-[11px] text-slate-500 dark:text-slate-400">
              <span className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                Zadavatel: já
              </span>
              <span className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                Zpracovatel: já
              </span>
              <span className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                Label: {createLabels.trim() || 'BE'}
              </span>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowCreateTicket(false)}
                disabled={creatingTicket}
                className="px-3.5 py-2 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
              >
                Zrušit
              </button>
              <button
                type="submit"
                disabled={creatingTicket || !createSummary.trim()}
                className="px-3.5 py-2 text-sm font-semibold rounded-xl bg-complic-600 text-white hover:bg-complic-700 disabled:opacity-50 shadow-soft inline-flex items-center gap-2"
              >
                {creatingTicket && (
                  <span className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                )}
                Vytvořit
              </button>
            </div>
          </form>
        </div>
      )}

      <div
        ref={layoutRef}
        className={`flex gap-0 max-w-full justify-center items-start p-3 md:p-4 h-full overflow-hidden ${
          isResizingPanels ? 'select-none cursor-col-resize' : ''
        }`}
      >
      <div className="relative flex-1 min-w-0 space-y-3 overflow-y-auto max-h-full pr-1">
      {/* Header */}
      <div className="flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">Worklog</h2>
          <p className="text-xs text-slate-500 mt-0.5">Zapisuj čas, táhni dny, spravuj tikety vpravo</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {(isMoving || isRefreshing || (loading && !!worklogData)) && (
            <div className="flex items-center gap-2 text-sm text-complic-700 animate-pulse">
              <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-complic-600 border-t-transparent"></div>
              <span>{isMoving ? 'Moving worklog...' : 'Syncing...'}</span>
            </div>
          )}

          {timesheetSubmitPeriod && (
            <button
              type="button"
              onClick={() => {
                const url = buildTimesheetsAppUrl(jiraUrl);
                if (url) {
                  window.open(url, '_blank', 'noopener,noreferrer');
                  setConfirmTimesheetSubmitted(true);
                  return;
                }
                // Forge needs /jira/apps/{appId}/{envId} — bare appId 404s
                setTimesheetsUrlDraft(getSavedTimesheetsAppUrl() || '');
                setTimesheetsUrlError(null);
                setShowTimesheetsUrlSetup(true);
              }}
              className="px-3 py-1.5 text-xs font-semibold rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/50 shadow-soft inline-flex items-center gap-1.5"
              title={
                buildTimesheetsAppUrl(jiraUrl)
                  ? `Otevřít Timesheets v Jiře a submitnout ${timesheetSubmitPeriod.label}`
                  : 'Nejdřív ulož odkaz na Timesheets z Jiry (jednou)'
              }
            >
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
              Submit {timesheetSubmitPeriod.label}
            </button>
          )}

          <button
            type="button"
            onClick={() => setShowTimesheet(true)}
            disabled={!worklogData || worklogData.tickets.length === 0}
            className="px-3 py-1.5 text-xs font-semibold rounded-full border border-slate-200 dark:border-slate-600 bg-white/90 dark:bg-slate-800/90 text-slate-700 dark:text-slate-100 hover:border-complic-300 hover:text-complic-700 disabled:opacity-40 shadow-soft"
            title="Timesheet z Jira worklogu za vybraný měsíc"
          >
            Timesheet
          </button>

          <div className="flex items-center gap-1 bg-white/90 dark:bg-slate-800/90 rounded-full shadow-soft px-1.5 py-1 border border-slate-200/80 dark:border-slate-600">
            <button
              onClick={() => changeMonth(-1)}
              className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition text-slate-700 dark:text-white text-sm"
            >
              ◀
            </button>
            <span className="font-semibold text-slate-800 dark:text-white min-w-[148px] text-center text-sm tracking-tight">
              {monthNames[selectedMonth - 1]} {selectedYear}
            </span>
            <button
              onClick={() => changeMonth(1)}
              className="w-8 h-8 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-700 rounded-full transition text-slate-700 dark:text-white text-sm"
            >
              ▶
            </button>
          </div>
        </div>
      </div>

      {/* Ticket search */}
      <div className="relative" ref={searchWrapRef}>
        <div className="flex items-center gap-2 bg-white/90 dark:bg-slate-800/90 rounded-2xl shadow-soft px-3.5 py-2.5 border border-slate-200/80 dark:border-slate-600">
          <svg className="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 18a7 7 0 100-14 7 7 0 000 14z" />
          </svg>
          <input
            type="text"
            value={ticketSearch}
            onChange={(e) => {
              setTicketSearch(e.target.value);
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder="Hledat tiket (key / název) — i mimo filtr…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-800 dark:text-white placeholder-slate-400 min-w-0"
          />
          {ticketSearch.trim() && (
            <button
              type="button"
              onClick={() => setTicketSearch('')}
              className="text-slate-400 hover:text-slate-700 dark:hover:text-white text-sm px-1"
              title="Zrušit hledání"
            >
              ×
            </button>
          )}
          {ticketSearch.trim() && outOfFilterSearchHits.length > 0 && (
            <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-lg px-1.5 py-0.5 whitespace-nowrap">
              +{outOfFilterSearchHits.length} mimo filtr
            </span>
          )}
          {loadingCatalog && (
            <div className="inline-block animate-spin rounded-full h-3.5 w-3.5 border-2 border-complic-500 border-t-transparent" />
          )}
        </div>
      </div>

      {error && !worklogData && (
        <div className="max-w-lg">
          <JiraErrorBanner
            message={error}
            onRetry={() => fetchWorklog()}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      {/* Worklog Table — skeleton only in this column so Tikety stay usable */}
      {loading && !worklogData ? (
        <div className="rounded-2xl overflow-hidden border border-slate-200/90 dark:border-slate-700 bg-white/90 dark:bg-slate-900/60 shadow-soft animate-pulse">
          <div className="h-10 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700" />
          <div className="h-8 bg-complic-50/80 dark:bg-complic-950/30 border-b border-slate-200 dark:border-slate-700" />
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="h-9 border-b border-slate-100 dark:border-slate-800 flex items-center gap-3 px-3"
            >
              <div className="h-3.5 bg-complic-200/70 dark:bg-complic-800 rounded w-16 shrink-0" />
              <div className="h-3 bg-slate-100 dark:bg-slate-700 rounded flex-1 max-w-xs" />
            </div>
          ))}
        </div>
      ) : worklogData ? (
      <div className="rounded-2xl overflow-x-auto border border-slate-200/90 dark:border-slate-700 bg-white/90 dark:bg-slate-900/60 shadow-soft">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0">
            <tr className="bg-slate-50/95 dark:bg-slate-800/95">
              <th className="border-b border-r border-slate-200 dark:border-slate-700 px-2 py-1.5 text-left font-bold text-slate-700 dark:text-slate-100 sticky left-0 bg-slate-50/95 dark:bg-slate-800/95 z-10 text-xs tracking-tight" style={{ minWidth: '330px', maxWidth: '330px', width: '330px' }}>
                Ticket
              </th>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const isWeekend = new Date(selectedYear, selectedMonth - 1, day).getDay() % 6 === 0;
                const holidayName = holidaysInMonth.get(day) || null;
                const isNonWorking = isWeekend || !!holidayName;
                const isToday =
                  selectedYear === now.getFullYear() &&
                  selectedMonth === (now.getMonth() + 1) &&
                  day === now.getDate();

                return (
                  <th
                    key={day}
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setDropdownPosition({
                        top: rect.bottom + window.scrollY,
                        left: rect.left + window.scrollX
                      });
                      setQuickActionsDay(day);
                    }}
                    className={`border-b border-r border-slate-200 dark:border-slate-700 px-0.5 py-1.5 text-center font-bold text-xs w-[40px] cursor-pointer transition ${
                      isToday
                        ? 'bg-complic-100 text-complic-800 dark:bg-complic-900/50 dark:text-complic-100'
                        : holidayName
                          ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300'
                          : isWeekend
                            ? 'bg-slate-100/80 text-slate-400 dark:bg-slate-800/50 dark:text-slate-400'
                            : 'text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                    title={
                      holidayName
                        ? `${holidayName} · den ${day}`
                        : isNonWorking
                          ? `Víkend · den ${day}`
                          : `Quick log · den ${day}`
                    }
                  >
                    {day}
                  </th>
                );
              })}
              <th className="border-b border-slate-200 dark:border-slate-700 px-1 py-1.5 text-center font-bold bg-complic-100 dark:bg-complic-900/40 text-complic-800 dark:text-complic-100 sticky right-0 z-10 text-xs" style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>
                Total
              </th>
            </tr>

            <tr className="bg-complic-50/95 dark:bg-complic-950/40 font-bold sticky top-[29px] z-[5]">
              <td className="border-b border-r border-slate-200 dark:border-slate-700 px-2 py-1 sticky left-0 bg-complic-50/95 dark:bg-complic-950/40 z-10 text-xs text-complic-800 dark:text-complic-100 font-bold">
                Daily Total
              </td>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const dailyTotal = visibleDailyTotals[day] || 0;
                const hours = dailyTotal / 3600;
                const isFullDay = hours === 8;
                const isNotFullDay = dailyTotal > 0 && !isFullDay;
                const holidayName = holidaysInMonth.get(day) || null;
                const isWeekend = new Date(selectedYear, selectedMonth - 1, day).getDay() % 6 === 0;
                // Don't flag incomplete day on holidays/weekends (0h is expected)
                const showIncomplete =
                  isNotFullDay && !holidayName && !isWeekend;

                return (
                  <td
                    key={day}
                    className={`border-b border-r border-slate-200 dark:border-slate-700 px-0.5 py-1 text-center text-xs font-semibold w-[40px] tabular-nums ${
                      holidayName && dailyTotal === 0
                        ? 'bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-300'
                        : dailyTotal === 0 || isFullDay
                          ? 'bg-complic-50/95 dark:bg-complic-950/40 text-complic-800 dark:text-complic-100'
                          : ''
                    } ${showIncomplete ? 'bg-amber-100 text-amber-800' : ''} ${
                      isNotFullDay && (holidayName || isWeekend) ? 'bg-amber-100/70 text-amber-800' : ''
                    }`}
                    title={holidayName || undefined}
                  >
                    {formatTime(dailyTotal)}
                  </td>
                );
              })}
              <td className="border-b border-slate-200 dark:border-slate-700 px-1 py-1 text-center bg-complic-100 dark:bg-complic-900/40 text-complic-800 dark:text-complic-100 sticky right-0 text-xs font-bold tabular-nums" style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>
                {formatTime(visibleTotalTime)}
              </td>
            </tr>
          </thead>
          <tbody>
            {sortedTickets.map((ticket) => (
              <tr
                key={ticket.key}
                className={`group ${
                  selectedTicketKey === ticket.key ? 'bg-complic-50/70 dark:bg-complic-900/20' : 'hover:bg-slate-50/80 dark:hover:bg-slate-800/40'
                }`}
              >
                <td
                  className="border-b border-r border-slate-100 dark:border-slate-700/80 px-2 py-1 sticky left-0 bg-white dark:bg-slate-900 z-10 cursor-pointer group-hover:bg-slate-50 dark:group-hover:bg-slate-800"
                  style={{ minWidth: '330px', maxWidth: '330px', width: '330px' }}
                  onClick={() => handleTicketSelect(ticket)}
                >
                  <div className="flex items-center gap-2">
                    <TicketKeyActions
                      ticketKey={ticket.key}
                      jiraUrl={jiraUrl}
                      keyClassName="font-mono text-sm font-extrabold text-complic-700 dark:text-complic-300 whitespace-nowrap"
                    />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-100 truncate hover:underline" title={ticket.summary}>
                      {ticket.summary}
                    </span>
                  </div>
                </td>
                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                  const timeLogged = ticket.by_day[day] || 0;
                  const isWeekend = new Date(selectedYear, selectedMonth - 1, day).getDay() % 6 === 0;
                  const holidayName = holidaysInMonth.get(day) || null;
                  const isDragSource = dragSource?.ticketKey === ticket.key && dragSource?.day === day;
                  const isDragTarget = dragSource && !(dragSource.ticketKey === ticket.key && dragSource.day === day);

                  return (
                    <td
                      key={day}
                      draggable={timeLogged > 0}
                      onDragStart={(e) => handleDragStart(e, ticket.key, day, timeLogged)}
                      onDragEnd={handleDragEnd}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, ticket.key, day)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setQuickWorklog({ ticketKey: ticket.key, day });
                      }}
                      style={{ userSelect: 'none' }}
                      className={`border-b border-r border-slate-100 dark:border-slate-700/80 px-0.5 py-1 text-center text-xs font-semibold w-[40px] transition-all tabular-nums ${
                        timeLogged > 0 ? 'cursor-move' : 'cursor-pointer'
                      } ${
                        timeLogged === 0 && holidayName
                          ? 'bg-rose-50/80 dark:bg-rose-950/30'
                          : timeLogged === 0 && isWeekend
                            ? 'bg-slate-50 dark:bg-slate-800/40'
                            : timeLogged === 0
                              ? 'bg-white dark:bg-slate-900'
                              : ''
                      } ${
                        isDragSource ? 'ring-2 ring-amber-400' : ''
                      } ${
                        isDragTarget && timeLogged === 0 ? 'bg-sky-100 ring-2 ring-sky-400' : ''
                      } ${
                        isDragTarget && timeLogged > 0 ? 'ring-2 ring-emerald-400' : ''
                      } ${
                        timeLogged > 0
                          ? 'bg-emerald-100 font-bold text-emerald-800 hover:ring-2 hover:ring-emerald-400'
                          : 'text-slate-400 hover:ring-2 hover:ring-complic-300 hover:bg-complic-50/50'
                      }`}
                      title={
                        holidayName
                          ? `${holidayName} · ${ticket.key}`
                          : timeLogged > 0
                            ? `Drag to move • Click to log time for ${ticket.key} on day ${day}`
                            : `Click to log time for ${ticket.key} on day ${day}`
                      }
                    >
                      {formatTime(timeLogged)}
                    </td>
                  );
                })}
                <td className="border-b border-slate-100 dark:border-slate-700/80 px-1 py-1 text-center font-bold bg-complic-50 dark:bg-complic-950/30 text-complic-800 dark:text-complic-100 sticky right-0 text-xs tabular-nums" style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>
                  {formatTime(ticket.my_time)}
                </td>
              </tr>
            ))}

            <tr className="bg-complic-50 dark:bg-complic-950/40 font-bold">
              <td className="border-t border-r border-slate-200 dark:border-slate-700 px-2 py-1.5 sticky left-0 bg-complic-50 dark:bg-complic-950/40 z-10 text-xs text-complic-800 dark:text-complic-100 font-bold" style={{ minWidth: '330px', maxWidth: '330px', width: '330px' }}>
                Daily Total
              </td>
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const dailyTotal = visibleDailyTotals[day] || 0;
                const hours = dailyTotal / 3600;
                const isFullDay = hours === 8;
                const isNotFullDay = dailyTotal > 0 && !isFullDay;
                const holidayName = holidaysInMonth.get(day) || null;
                const isWeekend = new Date(selectedYear, selectedMonth - 1, day).getDay() % 6 === 0;
                const showIncomplete = isNotFullDay && !holidayName && !isWeekend;

                return (
                  <td
                    key={day}
                    className={`border-t border-r border-slate-200 dark:border-slate-700 px-0.5 py-1.5 text-center text-xs font-semibold w-[40px] tabular-nums ${
                      holidayName && dailyTotal === 0
                        ? 'bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-300'
                        : dailyTotal === 0 || isFullDay
                          ? 'bg-complic-50 dark:bg-complic-950/40 text-complic-800 dark:text-complic-100'
                          : ''
                    } ${showIncomplete ? 'bg-amber-100 text-amber-800' : ''} ${
                      isNotFullDay && (holidayName || isWeekend) ? 'bg-amber-100/70 text-amber-800' : ''
                    }`}
                    title={holidayName || undefined}
                  >
                    {formatTime(dailyTotal)}
                  </td>
                );
              })}
              <td className="border-t border-slate-200 dark:border-slate-700 px-1 py-1.5 text-center bg-complic-100 dark:bg-complic-900/40 text-complic-800 dark:text-complic-100 sticky right-0 text-xs font-bold tabular-nums" style={{ width: '60px', minWidth: '60px', maxWidth: '60px' }}>
                {formatTime(visibleTotalTime)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      ) : null}

      {/* Search hits outside active filter (e.g. FE when filter is BE) */}
      {ticketSearch.trim() && outOfFilterSearchHits.length > 0 && (
        <div className="rounded-2xl border border-amber-200/90 dark:border-amber-800/60 bg-amber-50/50 dark:bg-amber-950/20 shadow-soft overflow-hidden">
          <div className="px-3.5 py-2.5 border-b border-amber-200/80 dark:border-amber-800/50 flex items-center gap-2">
            <span className="text-xs font-bold uppercase tracking-wide text-amber-800 dark:text-amber-200">
              Mimo filtr
            </span>
            <span className="text-[11px] text-amber-700/80 dark:text-amber-300/80">
              {outOfFilterSearchHits.length} tiket
              {outOfFilterSearchHits.length === 1 ? '' : 'ů'} odpovídá „{ticketSearch.trim()}“
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-amber-100 dark:divide-amber-900/40">
            {outOfFilterSearchHits.map((t) => (
              <div
                key={t.key}
                role="button"
                tabIndex={0}
                onClick={() => handleTicketSelect(t)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleTicketSelect(t);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3.5 py-2 text-left cursor-pointer transition ${
                  selectedTicketKey === t.key
                    ? 'bg-amber-100/80 dark:bg-amber-900/40'
                    : 'hover:bg-amber-100/60 dark:hover:bg-amber-900/30'
                }`}
                title={`Otevřít ${t.key}`}
              >
                <TicketKeyActions
                  ticketKey={t.key}
                  jiraUrl={jiraUrl}
                  keyClassName="font-mono text-sm font-extrabold text-complic-700 dark:text-complic-300 whitespace-nowrap"
                />
                <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate min-w-0 flex-1">
                  {t.summary}
                </span>
                {t.labels && t.labels.length > 0 && (
                  <div className="hidden sm:flex items-center gap-1 shrink-0">
                    {t.labels.slice(0, 3).map((label) => (
                      <span
                        key={label}
                        className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase bg-white/80 dark:bg-slate-800/80 text-slate-600 dark:text-slate-300 border border-slate-200/80 dark:border-slate-600"
                      >
                        {label}
                      </span>
                    ))}
                  </div>
                )}
                {t.assignee && (
                  <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate max-w-[7rem] shrink-0 hidden md:inline">
                    {t.assignee}
                  </span>
                )}
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-lg font-bold uppercase shrink-0 ${getStatusColor(t.status)}`}
                >
                  {t.status || '—'}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openLogForTicket(t.key);
                  }}
                  className="w-8 h-8 rounded-xl border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 flex items-center justify-center hover:bg-amber-100 dark:hover:bg-amber-900/40 transition shrink-0"
                  title={`Logovat čas · ${t.key}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l2.5 1.5M12 22a10 10 0 100-20 10 10 0 000 20z" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showTimesheetsUrlSetup && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setShowTimesheetsUrlSetup(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-panel border border-slate-200 dark:border-slate-700 w-full max-w-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white">
              Odkaz na Timesheets
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Jira potřebuje plnou adresu včetně environment ID (jinak 404). Jednou:
            </p>
            <ol className="text-sm text-slate-600 dark:text-slate-300 list-decimal ml-5 space-y-1">
              <li>
                V Jiře otevři <strong>Aplikace → Timesheets</strong>
              </li>
              <li>Zkopíruj URL z adresního řádku</li>
              <li>Vlož ji sem a ulož</li>
            </ol>
            <p className="text-[11px] text-slate-400 font-mono break-all">
              např. https://m2c-innovis.atlassian.net/jira/apps/879667b0-…/…………-env-id…………
            </p>
            <input
              autoFocus
              type="url"
              value={timesheetsUrlDraft}
              onChange={(e) => {
                setTimesheetsUrlDraft(e.target.value);
                setTimesheetsUrlError(null);
              }}
              placeholder="https://….atlassian.net/jira/apps/…/…"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-amber-400/50"
            />
            {timesheetsUrlError && (
              <p className="text-xs text-rose-600 dark:text-rose-400">{timesheetsUrlError}</p>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => setShowTimesheetsUrlSetup(false)}
                className="px-3 py-2 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Zrušit
              </button>
              <button
                type="button"
                onClick={() => {
                  const saved = saveTimesheetsAppUrl(timesheetsUrlDraft);
                  if (!saved) {
                    setTimesheetsUrlError(
                      'Neplatná URL — musí obsahovat /jira/apps/{uuid}/{uuid}'
                    );
                    return;
                  }
                  setShowTimesheetsUrlSetup(false);
                  window.open(saved, '_blank', 'noopener,noreferrer');
                  setConfirmTimesheetSubmitted(true);
                  addToast('Odkaz na Timesheets uložen', 'success');
                }}
                className="px-3 py-2 text-sm font-semibold rounded-xl bg-amber-500 text-white hover:bg-amber-600 shadow-soft"
              >
                Uložit a otevřít
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmTimesheetSubmitted && timesheetSubmitPeriod && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setConfirmTimesheetSubmitted(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-panel border border-slate-200 dark:border-slate-700 w-full max-w-sm p-5 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white">
              Timesheet {timesheetSubmitPeriod.label}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Otevřel jsem Timesheets v Jiře. Až tam klikneš Submit (když je Due), označ to tady jako
              hotové — tlačítko zmizí.
            </p>
            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setConfirmTimesheetSubmitted(false);
                  setTimesheetsUrlDraft(getSavedTimesheetsAppUrl() || '');
                  setTimesheetsUrlError(null);
                  setShowTimesheetsUrlSetup(true);
                }}
                className="px-3 py-2 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Změnit odkaz
              </button>
              <button
                type="button"
                onClick={() => setConfirmTimesheetSubmitted(false)}
                className="px-3 py-2 text-sm font-semibold rounded-xl border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Ještě ne
              </button>
              <button
                type="button"
                onClick={() => {
                  markTimesheetPeriodSubmitted(timesheetSubmitPeriod.key);
                  setTimesheetSubmitPeriod(null);
                  setConfirmTimesheetSubmitted(false);
                  addToast(`Timesheet ${timesheetSubmitPeriod.label} označen jako odeslaný`, 'success');
                }}
                className="px-3 py-2 text-sm font-semibold rounded-xl bg-amber-500 text-white hover:bg-amber-600 shadow-soft"
              >
                Už jsem submitnul
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Worklog Modal */}
      {quickWorklog && (
        <QuickWorklogModal
          ticketKey={quickWorklog.ticketKey}
          year={selectedYear}
          month={selectedMonth}
          day={quickWorklog.day}
          projectId={currentProject?.id}
          dayTotalSeconds={
            (worklogData &&
              (visibleDailyTotals[quickWorklog.day] ??
                worklogData.daily_totals?.[quickWorklog.day] ??
                0)) ||
            0
          }
          onClose={() => setQuickWorklog(null)}
          onSuccess={() => {
            // Keep modal + table mounted; sync grid in background
            fetchWorklog(true);
          }}
        />
      )}

      {/* Quick Actions Dropdown */}
      {quickActionsDay && dropdownPosition && (() => {
        const daySecs = visibleDailyTotals[quickActionsDay] || 0;
        const remainingHours = Math.round(Math.max(0, 8 - daySecs / 3600) * 100) / 100;
        const ticketsForFill = sortedTickets
          .filter((t) => (t.by_day[quickActionsDay] || 0) > 0)
          .slice(0, 6);
        const fillCandidates =
          ticketsForFill.length > 0
            ? ticketsForFill
            : sortedTickets.slice(0, 5);

        return (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => {
              setQuickActionsDay(null);
              setDropdownPosition(null);
            }}
          />

          <div
            className="fixed z-50 bg-white dark:bg-slate-900 rounded-2xl shadow-panel border border-slate-200 dark:border-slate-700 py-1.5 min-w-[240px] max-h-[70vh] overflow-y-auto"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`
            }}
          >
            <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-700 mb-1">
              <span className="text-xs font-semibold text-slate-500">
                Quick Log — Day {quickActionsDay}
              </span>
              {remainingHours > 0 && remainingHours < 8 && (
                <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5 tabular-nums">
                  Dnes {formatTime(daySecs)} · zbývá {remainingHours}h do 8h
                </div>
              )}
            </div>

            {remainingHours > 0 && (
              <>
                <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  Dopočítat do 8h (+{remainingHours}h)
                </div>
                {fillCandidates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-400 italic">
                    Žádný tiket v měsíci — klikni buňku v tabulce
                  </div>
                ) : (
                  fillCandidates.map((t) => (
                    <button
                      key={`fill-${t.key}`}
                      type="button"
                      onClick={() =>
                        handleQuickAction(
                          t.key,
                          remainingHours,
                          'Dopočet do 8h',
                          quickActionsDay
                        )
                      }
                      className="w-full text-left px-3 py-2 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition flex justify-between items-center gap-2"
                      title={t.summary}
                    >
                      <span className="text-sm font-mono font-semibold text-amber-800 dark:text-amber-200 truncate">
                        {t.key}
                      </span>
                      <span className="text-xs text-amber-600 dark:text-amber-400 tabular-nums shrink-0">
                        +{remainingHours}h
                      </span>
                    </button>
                  ))
                )}
                <div className="border-t border-slate-100 dark:border-slate-700 my-1" />
              </>
            )}

            <button
              onClick={() => handleQuickAction('PM3-8', 0.25, 'Standup', quickActionsDay)}
              className="w-full text-left px-3 py-2 hover:bg-complic-50 dark:hover:bg-complic-900/30 transition flex justify-between items-center"
            >
              <span className="text-sm text-slate-800 dark:text-white">Standup</span>
              <span className="text-xs text-slate-400 tabular-nums">0.25h</span>
            </button>

            <button
              onClick={() => handleQuickAction('PM3-8', 1, 'Planning/Refinement', quickActionsDay)}
              className="w-full text-left px-3 py-2 hover:bg-complic-50 dark:hover:bg-complic-900/30 transition flex justify-between items-center"
            >
              <span className="text-sm text-slate-800 dark:text-white">Planning/Refinement</span>
              <span className="text-xs text-slate-400 tabular-nums">1h</span>
            </button>

            <div className="border-t border-slate-100 dark:border-slate-700 my-1"></div>

            <button
              onClick={() => handleQuickAction('PM3-738', 1.25, 'AI', quickActionsDay)}
              className="w-full text-left px-3 py-2 hover:bg-complic-50 dark:hover:bg-complic-900/30 transition flex justify-between items-center"
            >
              <span className="text-sm text-slate-800 dark:text-white">AI</span>
              <span className="text-xs text-slate-400 tabular-nums">1.25h</span>
            </button>
          </div>
        </>
        );
      })()}

      {/* Ticket detail: viewport-tall (not clipped by short table); Tikety stay above overlay */}
      {selectedTicketKey && selectedTicketData && (
        <>
          <button
            type="button"
            aria-label="Zavřít detail"
            className="fixed inset-0 z-40 bg-transparent"
            onClick={() => {
              setSelectedTicketKey(null);
              setSelectedTicketData(null);
            }}
          />
          <div className="fixed z-50 top-16 bottom-3 left-3 w-[min(34rem,calc(100vw-22rem))] animate-[slideInFromLeft_0.25s_ease-out]">
            <div className="h-full shadow-panel rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 [&>div]:!w-full [&>div]:!h-full [&>div]:!max-h-none [&>div]:!rounded-2xl [&>div]:!sticky [&>div]:top-0 [&>div]:!border-0">
              <TicketDetailPanel
                ticketKey={selectedTicketKey}
                ticketData={{
                  key: selectedTicketData.key,
                  summary: selectedTicketData.summary,
                  status: selectedTicketData.status,
                  assignee: selectedTicketData.assignee,
                  priority: selectedTicketData.priority,
                  labels: selectedTicketData.labels,
                  sprint: selectedTicketData.sprint
                }}
                onClose={() => {
                  setSelectedTicketKey(null);
                  setSelectedTicketData(null);
                  setDetailExpandedSection(null);
                }}
                showTransitions={true}
                expandedSection={detailExpandedSection}
                initialExpandedSections={['description', 'comments']}
                assigneeOptions={preferredAssignees}
                assigneeOtherOptions={otherAssignees}
                projectStatuses={projectStatuses}
                onAssigneeChange={applyAssigneeLocally}
                onSprintChange={applySprintLocally}
                onSummaryChange={applySummaryLocally}
                onTransition={async (transitionId) => {
                  try {
                    const res = await axios.post(
                      `${API_URL}/api/tickets/${selectedTicketKey}/transition`,
                      null,
                      {
                        params: {
                          transition_id: transitionId,
                          project_id: currentProject?.id,
                        },
                      }
                    );
                    const newStatus = res.data?.status;
                    if (newStatus) {
                      setAllTickets((prev) =>
                        prev.map((t) =>
                          t.key === selectedTicketKey ? { ...t, status: newStatus } : t
                        )
                      );
                      setSelectedTicketData((prev) =>
                        prev ? { ...prev, status: newStatus } : prev
                      );
                    }
                    setListTransitions((prev) => {
                      const next = { ...prev };
                      delete next[selectedTicketKey];
                      return next;
                    });
                    void refreshCatalog();
                  } catch (error) {
                    addToast(notifyJiraError(error), 'error');
                    throw error;
                  }
                }}
              />
            </div>
          </div>
        </>
      )}
      </div>

      {/* Drag handle between worklog and tickets */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Změnit šířku panelu Tikety"
        title="Táhni pro změnu šířky"
        onMouseDown={(e) => {
          e.preventDefault();
          setIsResizingPanels(true);
        }}
        className={`w-2.5 shrink-0 self-stretch flex items-stretch justify-center cursor-col-resize group relative z-[61] ${
          isResizingPanels ? 'bg-complic-500/10' : ''
        }`}
      >
        <div
          className={`w-0.5 my-10 rounded-full transition-colors ${
            isResizingPanels
              ? 'bg-complic-500'
              : 'bg-slate-300/80 dark:bg-slate-600 group-hover:bg-complic-400'
          }`}
        />
      </div>

      {/* Right column: filtered ticket list */}
      <aside
        style={{ width: ticketsPanelWidth }}
        className="min-w-0 shrink-0 sticky top-2 self-start max-h-[calc(100vh-7rem)] flex flex-col bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-soft overflow-hidden relative z-[60]"
      >
          <div className="px-3.5 py-3 border-b border-slate-100 dark:border-slate-700 space-y-2 shrink-0 bg-slate-50/60 dark:bg-slate-800/40">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-sm font-bold tracking-tight text-slate-800 dark:text-slate-100">
                  Tikety
                </span>
                <button
                  type="button"
                  onClick={() => void openCreateTicketModal()}
                  className="w-6 h-6 rounded-lg flex items-center justify-center text-complic-600 dark:text-complic-300 hover:bg-complic-50 dark:hover:bg-complic-900/40 border border-transparent hover:border-complic-200 dark:hover:border-complic-700 transition text-lg font-bold leading-none"
                  title="Založit nový tiket"
                  aria-label="Založit nový tiket"
                >
                  +
                </button>
              </div>
              <span className="text-[11px] font-medium text-slate-400 tabular-nums">
                {loadingCatalog ? '…' : filteredCatalogTickets.length}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <button
                type="button"
                onClick={() => selectFilterGroup(null)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                  selectedGroupId === null
                    ? 'bg-complic-600 text-white shadow-soft'
                    : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:border-complic-300'
                }`}
              >
                All
              </button>
              {filterGroups.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => selectFilterGroup(g.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition truncate max-w-[140px] ${
                    selectedGroupId === g.id
                      ? 'bg-complic-600 text-white shadow-soft'
                      : 'bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 hover:border-complic-300'
                  }`}
                  title={g.name}
                >
                  {g.name}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto flex-1 min-h-0">
            {loadingCatalog && filteredCatalogTickets.length === 0 ? (
              <div className="px-3.5 py-3 text-xs text-slate-400">Načítám…</div>
            ) : filteredCatalogTickets.length === 0 ? (
              <div className="px-3.5 py-3 text-xs text-slate-400 italic">
                {ticketSearch.trim() ? 'Žádné tikety neodpovídají hledání' : 'Žádné tikety ve filtru'}
              </div>
            ) : (
              catalogSprintNames.map((sprintName) => (
                <div key={sprintName}>
                  <div className="sticky top-0 z-[1] px-3.5 py-2 bg-slate-100/90 dark:bg-slate-800/95 backdrop-blur border-b border-slate-200/80 dark:border-slate-700 flex items-center gap-1.5">
                    <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-300 truncate">
                      {sprintName}
                    </span>
                    <span className="text-[10px] text-slate-400 ml-auto tabular-nums">
                      {catalogBySprint[sprintName].length}
                    </span>
                  </div>
                  {catalogBySprint[sprintName].map((t) => (
                    <div
                      key={t.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleTicketSelect(t)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleTicketSelect(t);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-3.5 py-2.5 text-left cursor-pointer border-b border-slate-100 dark:border-slate-800 last:border-0 transition ${
                        selectedTicketKey === t.key
                          ? 'bg-complic-50 dark:bg-complic-900/30 ring-1 ring-inset ring-complic-200'
                          : isHighlightedCatalogTicket(t)
                            ? 'bg-white dark:bg-slate-900 hover:bg-complic-50/70 dark:hover:bg-complic-900/20'
                            : 'bg-slate-200 dark:bg-slate-800/80 hover:bg-slate-300/80 dark:hover:bg-slate-800'
                      }`}
                      title={`Otevřít ${t.key}`}
                    >
                      <TicketKeyActions
                        ticketKey={t.key}
                        jiraUrl={jiraUrl}
                        keyClassName="font-mono text-sm font-extrabold text-complic-700 dark:text-complic-300 whitespace-nowrap"
                      />
                      <span className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate min-w-0 flex-1">
                        {t.summary}
                      </span>
                      <div
                        className="relative shrink-0"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openLogForTicket(t.key);
                          }}
                          className="w-8 h-8 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-300 flex items-center justify-center hover:border-complic-400 hover:text-complic-600 dark:hover:text-complic-300 hover:bg-complic-50/60 dark:hover:bg-complic-900/20 transition"
                          title={`Logovat čas · ${t.key}`}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l2.5 1.5M12 22a10 10 0 100-20 10 10 0 000 20z" />
                          </svg>
                        </button>
                      </div>
                      <div
                        className="relative w-28 shrink-0"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={(e) => handleStatusClick(t.key, e)}
                          className={`text-xs px-2.5 py-1.5 rounded-lg font-bold whitespace-nowrap uppercase transition cursor-pointer flex items-center justify-center gap-1 w-full shadow-sm ${getStatusColor(t.status)}`}
                          title={t.status || undefined}
                        >
                          <span className="truncate">{t.status || '—'}</span>
                          <span className="text-[10px] leading-none opacity-80">▼</span>
                        </button>
                        {showListTransitions === t.key && (
                          <div className="absolute z-50 mt-1 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-panel min-w-[180px] max-h-72 overflow-y-auto overflow-hidden">
                            {loadingListTransitions === t.key && !listTransitions[t.key] ? (
                              <div className="px-3 py-2 text-xs text-slate-500">Načítám…</div>
                            ) : (
                              <>
                                {buildStatusPickerItems(
                                  projectStatuses,
                                  listTransitions[t.key] || [],
                                  t.status
                                ).map((item) => (
                                  <button
                                    key={item.status}
                                    type="button"
                                    disabled={item.isCurrent || !item.transitionId}
                                    onClick={(e) => {
                                      if (!item.transitionId) return;
                                      handleListTransition(
                                        t.key,
                                        item.transitionId,
                                        item.status,
                                        e,
                                        item.status
                                      );
                                    }}
                                    className={`block w-full text-left px-3 py-2 text-xs transition ${
                                      item.isCurrent
                                        ? 'bg-complic-50 dark:bg-complic-900/30 text-complic-800 dark:text-complic-200 font-semibold'
                                        : 'hover:bg-complic-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                                    }`}
                                    title={item.isCurrent ? 'Aktuální stav' : item.status}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span>{item.status}</span>
                                      {item.isCurrent && (
                                        <span className="text-[10px] text-complic-600">●</span>
                                      )}
                                    </div>
                                  </button>
                                ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div
                        className="relative shrink-0"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          disabled={updatingAssignee}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowListTransitions(null);
                            setShowComplicMenu(null);
                            setShowOtherAssignees(false);
                            setShowAssigneeMenu((prev) => (prev === t.key ? null : t.key));
                          }}
                          className={`w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-black tracking-wide shrink-0 shadow-soft hover:ring-2 hover:ring-complic-300 transition ${getAvatarColor(t.assignee)}`}
                          title={t.assignee ? `Změnit: ${t.assignee}` : 'Přiřadit osobu'}
                        >
                          {getInitials(t.assignee)}
                        </button>
                        {showAssigneeMenu === t.key && (
                          <div className="absolute z-50 mt-1 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-panel min-w-[170px] max-h-56 overflow-y-auto">
                            <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-700 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                              Assignee
                            </div>
                            <button
                              type="button"
                              onClick={(e) => handleAssigneeChange(t.key, null, e)}
                              className={`block w-full text-left px-3 py-2 text-xs hover:bg-complic-50 dark:hover:bg-slate-700 transition ${
                                !t.assignee
                                  ? 'font-semibold text-complic-600'
                                  : 'text-slate-700 dark:text-slate-200'
                              }`}
                            >
                              Unassigned
                            </button>
                            {preferredAssignees.map((name) => (
                              <button
                                key={name}
                                type="button"
                                onClick={(e) => handleAssigneeChange(t.key, name, e)}
                                className={`block w-full text-left px-3 py-2 text-xs hover:bg-complic-50 dark:hover:bg-slate-700 transition ${
                                  t.assignee === name
                                    ? 'font-semibold text-complic-600'
                                    : 'text-slate-700 dark:text-slate-200'
                                }`}
                              >
                                {name}
                              </button>
                            ))}
                            {otherAssignees.length > 0 && (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowOtherAssignees((v) => !v);
                                  }}
                                  className="w-full text-left px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 border-t border-slate-100 dark:border-slate-700"
                                >
                                  {showOtherAssignees
                                    ? 'Skrýt ostatní'
                                    : `Ostatní (${otherAssignees.length})`}
                                </button>
                                {showOtherAssignees &&
                                  otherAssignees.map((name) => (
                                    <button
                                      key={name}
                                      type="button"
                                      onClick={(e) => handleAssigneeChange(t.key, name, e)}
                                      className={`block w-full text-left px-3 py-2 text-xs hover:bg-complic-50 dark:hover:bg-slate-700 transition ${
                                        t.assignee === name
                                          ? 'font-semibold text-complic-600'
                                          : 'text-slate-500 dark:text-slate-400'
                                      }`}
                                    >
                                      {name}
                                    </button>
                                  ))}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                      <div
                        className="relative shrink-0"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowListTransitions(null);
                            setShowAssigneeMenu(null);
                            setShowComplicMenu((prev) => (prev === t.key ? null : t.key));
                          }}
                          className="w-8 h-8 rounded-xl border border-complic-400/70 dark:border-complic-400/50 text-complic-600 dark:text-complic-300 text-xs font-bold flex items-center justify-center bg-transparent hover:bg-complic-50/70 dark:hover:bg-complic-900/30 transition"
                          title={`Complic · ${t.key}`}
                        >
                          C
                        </button>
                        {showComplicMenu === t.key && (
                          <div className="absolute z-50 mt-1 right-0 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-panel min-w-[170px] overflow-hidden">
                            <div className="px-3 py-1.5 border-b border-slate-100 dark:border-slate-700 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                              Complic · {t.key}
                            </div>
                            {(
                              [
                                { mode: 'full', label: 'Full (plan → PR)', hint: 'Kompletní developer' },
                                { mode: 'plan', label: 'Plan', hint: 'Jen plán' },
                                { mode: 'implement', label: 'Implement', hint: 'Implementace' },
                                { mode: 'push', label: 'Push + PR', hint: 'Push a pull request' },
                              ] as const
                            ).map((action) => (
                              <button
                                key={action.mode}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowComplicMenu(null);
                                  onStartComplic?.(t.key, action.mode);
                                }}
                                className="block w-full text-left px-3 py-2 hover:bg-complic-50 dark:hover:bg-complic-900/30 transition"
                              >
                                <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">{action.label}</div>
                                <div className="text-[10px] text-slate-400">{action.hint}</div>
                              </button>
                            ))}
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowComplicMenu(null);
                                window.dispatchEvent(
                                  new CustomEvent('complic-open-conversation', {
                                    detail: { ticketKey: t.key },
                                  })
                                );
                              }}
                              className="block w-full text-left px-3 py-2 border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
                            >
                              <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">Jen otevřít chat</div>
                              <div className="text-[10px] text-slate-400">Bez spuštění agenta</div>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>
        </aside>
      </div>
    </>
  );
};

export default Worklog;
